import React from "react";
import { useAppSelector } from "@/hooks/hook";
import { format, parseISO } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { RiCircleFill, RiFlagFill } from "react-icons/ri";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { USFormatePriceWithDecimal } from "@thdinc/hpf-shared";
import { getTimeDifference, splitEventDateAndTime, timeToAmPm } from "@/helpers/TimeFormat";
import clockIcon from "../../../assets/icons/clockIcon.svg";
import userIcon from "../../../assets/icons/userIcon.svg";
import { Booking } from "../Interface";
import Cup from "./Cup";

const EventCards = ({ data }: { data: Booking }) => {
    const user = useAppSelector((store) => store.user);
    const {
        id,
        auto_selected,
        first_name,
        last_name,
        discount_total,
        producttotal,
        service_total,
        start_time,
        end_time,
        timezone,
        guests,
        bartenders,
        btc_name,
        packageType,
        max_estimated_alcohol,
        is_quote_booking,
        tips_total,
        estimated_drinks_flagged
    } = data;

    return (
        <div
            className={`p-3 rounded-xl bg-white ${auto_selected ? "border border-primary-red" : "border border-green-500 "
                }`}
        >
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">
                    {first_name + " " + last_name}
                </h2>
                <p className={` text-3xl text-primary-red font-bold`}>
                    $
                    {discount_total !== 0
                        ? USFormatePriceWithDecimal(
                            (
                                is_quote_booking ? (((service_total + tips_total) / 100)) : ((((service_total + tips_total) - discount_total) / 100))
                            )
                        )
                        : USFormatePriceWithDecimal((service_total + tips_total) / 100)}{" "}
                </p>
            </div>
            <div>
                {user.roles && <h2 className="text-lg font">{"BTC: " + btc_name}</h2>}
            </div>

            <p className=" text-gray-500">
                {" "}
                {start_time && format(parseISO(start_time), "PPPP")}
            </p>
            <div className="flex flex-wrap gap-2 items-center my-1">
                {auto_selected ? (
                    <p className="text-[#FF6100] text-[10px] font-bold flex items-center gap-1 whitespace-nowrap">
                        <RiCircleFill />
                        Unassigned
                    </p>
                ) : (
                    <p className="text-[#00B69B] text-[10px] font-bold flex items-center gap-1 whitespace-nowrap">
                        <RiCircleFill />
                        Assigned
                    </p>
                )}
            </div>
            <p className="text-primary-red text-xs font-semibold ">
                {is_quote_booking ? splitEventDateAndTime(start_time).formattedTime +
                    " To " +
                    splitEventDateAndTime(end_time).formattedTime : timeToAmPm(start_time, timezone) +
                    " To " +
                timeToAmPm(end_time, timezone)}
            </p>
            <div className="flex justify-between flex-wrap gap-2 items-end mt-5">
                <div className="flex items-center justify-start gap-5 flex-1 min-w-0">
                    <div className="bg-p-gray px-4 py-1 flex gap-2 items-center rounded-full">
                        {/* <BsClock size={14} /> */}
                        <img src={clockIcon} />
                        <p className="text-sm font-semibold">
                            {
                                getTimeDifference(
                                    toZonedTime(start_time, timezone),
                                    toZonedTime(end_time, timezone)
                                ).hours
                            }{" "}
                            <span className="text-xs">hrs</span>{" "}
                        </p>
                    </div>
                    <div className="bg-p-gray px-4 py-1 flex gap-2 items-center rounded-full">
                        {/* <LiaUser size={17} /> */}
                        <img src={userIcon} />
                        <p className="text-sm font-semibold">{guests}</p>
                    </div>
                    {max_estimated_alcohol > 0 && <div className="bg-p-gray px-4 py-[6px] flex items-center rounded-full">
                        {/* <LiaUser size={17} /> */}
                        <Cup active={true} />
                    </div>}
                    {estimated_drinks_flagged && (
                        <>
                            <span
                                data-tooltip-id={`estimated-drinks-flag-${id}`}
                                className="inline-flex items-center cursor-pointer"
                            >
                                <RiFlagFill className="text-red-600" size={16} />
                            </span>
                            <ReactTooltip
                                id={`estimated-drinks-flag-${id}`}
                                place="bottom"
                                content="Cocktail Qty 30%+ Over Estimate"
                            />
                        </>
                    )}
                </div>
                {/* Badges on top row; info icon + avatars on row below (not same row as Assigned/Unassigned) */}
                <div className="flex flex-col items-end gap-2 shrink-0 ml-2">
                    <div className="flex flex-wrap gap-2 items-center justify-end">
                        {is_quote_booking &&
                            <p className="text-gray-400 text-[12px] font-bold flex items-center gap-1 whitespace-nowrap">
                                Custom Quote
                            </p>
                        }
                        {packageType === "custom_package" && (
                            <p className="text-gray-400 text-[12px] font-bold flex items-center gap-1 whitespace-nowrap">
                                Custom Package
                            </p>
                        )}
                        {packageType === "standard" && (
                            <p className="text-gray-400 text-[12px] font-bold flex items-center gap-1 whitespace-nowrap">
                                Standard
                            </p>
                        )}
                    </div>
                    <div className="flex flex-row items-center justify-end gap-2">
                        {/* <Popover className="relative inline-flex shrink-0">
                            <PopoverButton
                                type="button"
                                className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 transition"
                            >
                                <FiInfo className="text-gray-600" size={20} />
                            </PopoverButton>

                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-200"
                                enterFrom="opacity-0 translate-y-1"
                                enterTo="opacity-100 translate-y-0"
                                leave="transition ease-in duration-150"
                                leaveFrom="opacity-100 translate-y-0"
                                leaveTo="opacity-0 translate-y-1"
                            >
                                <PopoverPanel
                                    transition
                                    anchor="bottom"
                                    className="absolute z-50 mt-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-md text-sm text-gray-700"
                                >
                                    All your bartenders are already booked for this date.
                                </PopoverPanel>
                            </Transition>
                        </Popover> */}
                        <div className="flex flex-row items-center">
                            {bartenders.length > 0 ? (
                                bartenders.map((br, index) => (
                                    <img
                                        key={br.id ?? index}
                                        className={`rounded-full object-cover w-8 h-8 border-2 border-white shrink-0 ${index > 0 ? "-ml-3" : ""}`}
                                        src={br.avatar}
                                        alt=""
                                    />
                                ))
                            ) : (
                                <span
                                    className="inline-flex w-8 h-8 rounded-full bg-gray-100 border-2 border-dashed border-gray-200"
                                    aria-hidden
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventCards;
